import { create } from 'zustand'
import type { Session, User } from '@supabase/supabase-js'
import { supabase } from '@/lib/supabase'
import { logger } from '@/lib/logger'
import type { Database } from '@/lib/types/database'

type Profile = Database['public']['Tables']['profiles']['Row']

interface AuthState {
  user: User | null
  session: Session | null
  profile: Profile | null
  isLoading: boolean
  isAdmin: boolean
  isVenueOrOrganizer: boolean
  setSession: (session: Session | null) => void
  setProfile: (profile: Profile | null) => void
  setLoading: (loading: boolean) => void
  refreshProfile: () => Promise<void>
  reset: () => void
}

// Shared by provider and explicit refreshes; account changes invalidate pending reads.
let profileReadVersion = 0

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  session: null,
  profile: null,
  isLoading: true,
  isAdmin: false,
  isVenueOrOrganizer: false,
  setSession: (session) => {
    const newUser = session?.user ?? null
    const current = get().user
    const changedAccount = current?.id !== newUser?.id
    if (changedAccount) profileReadVersion++
    // Preserve the user object's referential identity across token refreshes to
    // avoid needless re-renders, BUT swap in the fresh object when an
    // identity-visible field changes (e.g. email after a confirmed email
    // change), so the UI reflects it immediately.
    const keepCurrent =
      !!current && current.id === newUser?.id && current.email === newUser?.email
    set({
      session,
      user: keepCurrent ? current : newUser,
      ...(changedAccount ? { profile: null, isAdmin: false, isVenueOrOrganizer: false } : {}),
    })
  },
  setProfile: (profile) => {
    if (profile && profile.id !== get().user?.id) return
    profileReadVersion++
    set({ profile, isAdmin: profile?.role === 'admin', isVenueOrOrganizer: profile?.role === 'organizer' || profile?.role === 'venue' })
  },
  setLoading: (isLoading) => set({ isLoading }),
  refreshProfile: async () => {
    const userId = get().user?.id
    if (!userId) return
    const version = ++profileReadVersion
    try {
      const { data, error } = await supabase.from('profiles').select('*').eq('id', userId).single()
      if (error) throw error
      if (!data || data.id !== userId) throw new Error('profile_read_unacknowledged')
      if (version !== profileReadVersion || get().user?.id !== userId) return
      set({ profile: data, isAdmin: data.role === 'admin', isVenueOrOrganizer: data.role === 'organizer' || data.role === 'venue' })
    } catch (error) {
      // Retain the current account's acknowledged profile on a failed refresh.
      logger.error('Failed to refresh profile:', error)
    }
  },
  reset: () => {
    profileReadVersion++
    set({
      user: null,
      session: null,
      profile: null,
      isLoading: false,
      isAdmin: false,
      isVenueOrOrganizer: false,
    })
  },
}))
